<template>
  <div>
    <v-dialog
      v-model="open"
      fullscreen
      persistent
      hide-overlay
      scrollable
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar dark dense color="primary" class="pl-6">
            <v-toolbar-title>{{ modalTitle }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn dark text @click="open = false">{{ $t("Cancel") }}</v-btn>
              <v-btn dark text @click="save" :loading="saving">{{
                $t("Save")
              }}</v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pt-1">
          <v-row>
            <v-col>
              <v-form>
                <v-row align="center">
                  <v-col cols="9">
                    <v-select
                      :label="$t('ContentType')"
                      v-model="contentTypeId"
                      @change="loadFields(false)"
                      :items="contentTypeList"
                      item-text="text"
                      item-value="id"
                      :error-messages="contentTypeErrors"
                      @input="$v.contentTypeId.$touch()"
                      @blur="$v.contentTypeId.$touch()"
                    >
                      <template v-slot:append-outer>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              class="pb-2"
                              icon
                              v-on="on"
                              @click="loadFields(true)"
                              v-show="contentTypeId"
                            >
                              <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("ResetFields") }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="3">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          class="pb-2"
                          icon
                          v-on="on"
                          @click="changeLayout(6)"
                        >
                          <v-icon>mdi-view-grid</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("Grid") }}</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          class="pb-2"
                          icon
                          v-on="on"
                          @click="changeLayout(12)"
                        >
                          <v-icon>mdi-view-list</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("List") }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          class="pb-2"
                          icon
                          v-on="on"
                          @click="scanDocument"
                        >
                          <v-icon>mdi-scanner</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("ScanDocument") }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-divider />
                <v-row
                  justify="center"
                  align="center"
                  v-show="loading || recognizing"
                >
                  <div class="text-center">
                    <div>
                      <v-progress-circular
                        class="my-4"
                        :size="50"
                        width="5"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                    </div>
                    <p v-show="loading" class="display-1">
                      {{ $t("Loading") }}...
                    </p>
                    <p v-show="recognizing" class="display-1">
                      {{ $t("DocumentTreatedFieldExtraction") }}...
                    </p>
                  </div>
                </v-row>
                <v-row v-if="!loading && !recognizing">
                  <v-col
                    :md="fieldsLayout"
                    v-for="(field, index) in document.fields"
                    v-bind:key="`document-modal-field-${field.defFieldId}`"
                    v-show="field.isVisible"
                  >
                    <DocumentField
                      :ref="field.code"
                      :validator.sync="$v"
                      :index="index"
                      :field="field"
                      :documentId="document.id"
                      @on-field-change="onFieldChange"
                      :canAddData="canAddData"
                      :style="{
                        'background-image': isRecognisedField(field.code)
                          ? 'linear-gradient(180deg, #CEFAD0,rgba(255,0,0,0) ,rgba(255,0,0,0),rgba(255,0,0,0))'
                          : isNotRecognisedField(field.code)
                          ? 'linear-gradient(180deg, #FBF4D1,rgba(255,0,0,0) ,rgba(255,0,0,0),rgba(255,0,0,0))'
                          : '',
                      }"
                      color="#83f28f"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
            <v-col>
              <VueDropzone
                id="dropZone"
                :ref="dropZoneKey"
                :options="dropzoneOptions"
                @vdropzone-success="onFileSuccess"
                @vdropzone-file-added="onFileAdded"
                :useCustomSlot="true"
                :include-styling="false"
                @vdropzone-thumbnail="thumbnail"
                v-show="!isDragAndDropDocument"
              >
                <v-hover v-slot="{ hover }">
                  <v-sheet
                    :elevation="hoverDropzone || hover ? 1 : 0"
                    color="grey lighten-4"
                    style="border: 1px dashed #ccc !important; cursor: pointer"
                    @dragover="dragover"
                    @dragleave="dragleave"
                    @drop="dragleave"
                  >
                    <v-row
                      justify="center"
                      align="center"
                      style="min-height: 90px"
                    >
                      <div class="text-center">
                        <v-icon x-large>mdi-cloud-upload</v-icon>
                        <p class="display-1">
                          <span>{{ $t("DropzoneMessage") }}</span>
                        </p>
                      </div>
                    </v-row>
                  </v-sheet>
                </v-hover>
              </VueDropzone>
              <AttachmentList
                v-show="
                  !showViewer &&
                  (attachments.length > 0 || originalAttachment.fileId > 0)
                "
                ref="attachmentList"
                :attachments="attachments"
                :originalAttachment="originalAttachment"
                :documentId="document.id"
                @onPreview="loadFile"
                @onRemove="removeFile"
                :allowDelete="!isDragAndDropDocument"
              />
              <v-card v-show="showViewer" class="pt-1">
                <v-app-bar flat dense color="grey lighten-4">
                  <v-btn icon @click="showViewer = false" class="ml-1">
                    <v-icon large>mdi-arrow-left</v-icon>
                  </v-btn>
                  <span>{{ selectedFileTitle }}</span>
                </v-app-bar>
                <v-card-text class="pa-0">
                  <PdfViewer :ref="viewerKey" :height="getModalHeight" />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <ScannerModal
            :ref="scannerModalKey"
            @on-scanned="onDocumentScanned"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar
      top
      center
      color="success"
      :timeout="6000"
      v-model="showDocumentCreatedToast"
    >
      {{ $t("DocumentCreatedSuccessfully") }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="viewCreatedDocuments">
          {{ $t("ViewDocuments") }}
        </v-btn>
        <v-btn
          dark
          v-bind="attrs"
          @click.native="showDocumentCreatedToast = false"
          icon
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import DocumentService from "@/services/DocumentService";
import ContentTypeService from "@/services/ContentTypeService";
import Document from "@/ecm/models/document/Document";
import Attachment from "@/ecm/models/document/Attachment";
import DocumentField from "@/components/document/documentModal/field/DocumentField";
import Utils from "@/ecm/common/Utils";
import { EventBus } from "@/eventBus.js";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import AttachmentList from "@/components/document/documentModal/attachment/AttachmentList";
import ScannerModal from "@/components/document/documentModal/scanner/ScannerModal";
import PdfViewer from "@/components/document/PdfViewer";
import SearchType from "@/ecm/variables/SearchType";
import ConditionItem from "@/ecm/models/search/ConditionItem";

export default {
  components: {
    DocumentField,
    VueDropzone: vue2Dropzone,
    PdfViewer,
    AttachmentList,
    ScannerModal,
  },
  created() {
    DocumentService.getSupportedFileFormats().then((data) => {
      this.dropzoneOptions.acceptedFiles = data.join(",");
    });
    this.$v.$reset();
  },
  data() {
    const url = `${this.$store.getters["account/apiUrl"]}/api/document/upload-attachment`;

    return {
      scannerModalKey: "scannerModalLicence",
      createdDocumentsIds: [],
      showDocumentCreatedToast: false,
      isDragAndDropDocument: false,
      isBasketDocument: false,
      contentTypeList: [],
      attachments: [],
      originalAttachment: {
        fileId: 0,
        fileName: "",
        isLoading: false,
        size: 0,
        extension: "",
        canDisplay: false,
      },
      open: false,
      selectedTab: 0,
      fieldsLayout: 6,
      selectedFile: null,
      contentTypeId: null,
      document: new Document(),
      loading: false,
      recognizing: false,
      recognisedFields: [],
      notRecognisedFields: [],
      saving: false,
      canAddData: false,
      showViewer: false,
      selectedFileTitle: "",
      viewerKey: "documentListPdfViewer",
      dropZoneKey: "drop-zone",
      hoverDropzone: false,
      dropzoneOptions: {
        previewsContainer: false,
        uploadMultiple: true,
        parallelUploads: 20,
        url: url,
        timeout: 60000, //30 seconds
        maxFiles: 20,
        maxFilesize: 1000, //10 MB
        destroyDropzone: true,
        headers: {
          Authorization: "bearer " + this.$store.getters["account/token"],
        },
        addRemoveLinks: true,
        acceptedFiles: "",
        dictRemoveFile: this.$t("Remove"),
        dictInvalidFileType: this.$t("DropzoneInvalidFileType"),
      },
    };
  },
  mixins: [validationMixin],
  validations: {
    contentTypeId: { required },
    document: {
      contentType: { required },
      fields: {
        $each: {
          value: {
            required: requiredIf(function (model) {
              return model.isRequired;
            }),
          },
        },
      },
    },
  },
  computed: {
    scannerModalInstance() {
      return this.$refs[this.scannerModalKey];
    },
    contentTypeErrors() {
      const errors = [];
      if (!this.$v.contentTypeId.$dirty) return errors;
      !this.$v.contentTypeId.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    modalTitle() {
      if (this.isDragAndDropDocument) return this.$t("Clone");
      return this.document.id == 0
        ? this.$t("NewDocument")
        : this.$t("EditDocument");
    },
    dropZone() {
      return this.$refs[this.dropZoneKey];
    },
    viewerInstance() {
      return this.$refs[this.viewerKey];
    },
    getModalHeight() {
      return screen.height;
    },
    isRecognisedField() {
      return (code) => this.recognisedFields.includes(code);
    },
    isNotRecognisedField() {
      return (code) => this.notRecognisedFields.includes(code);
    },
  },
  methods: {
    onDocumentScanned(attachment) {
      this.attachments = [...this.attachments, attachment];
    },
    scanDocument() {
      this.scannerModalInstance.show();
    },
    onFieldChange(field) {
      if (field.fieldsThatDependOn.length > 0)
        this.$nextTick(() => {
          this.refreshList(field.fieldsThatDependOn);
        });
    },
    async loadContentTypeList() {
      this.contentTypeList = await ContentTypeService.getList(true, true);
    },
    refreshList(fieldsToRefresh) {
      fieldsToRefresh.forEach((fieldCode) => {
        const fieldToRefresh = this.document.fields.filter(
          (f) => f.code == fieldCode
        )[0];

        const parameters = [];

        fieldToRefresh.fieldsOnWhichDepends.forEach((paramCode) => {
          var paramValue = this.document.fields.filter(
            (f) => f.code == paramCode
          )[0].value;

          var multiValue = false;
          if (Array.isArray(paramValue)) {
            paramValue = paramValue.filter((x) => x).join(",");
            multiValue = true;
          }
          parameters.push({
            Name: paramCode,
            Value: paramValue || "",
            Multivalue: multiValue,
          });
        });

        EventBus.$emit("refreshListElements" + fieldToRefresh.code, parameters);
      });
    },
    changeLayout(layout) {
      this.fieldsLayout = layout;
    },
    async loadFields(refresh) {
      if (!this.contentTypeId) {
        return;
      }
      this.loading = true;
      this.recognisedFields = [];
      this.notRecognisedFields = [];
      let documentId = this.document.id;
      let fields = [];
      if (this.document.contentType != null && !refresh) {
        fields = this.document.toDto([]).Fields;
      }
      var dto = await DocumentService.getStructureWithData(
        this.contentTypeId,
        fields
      );
      this.document = new Document(dto);
      if (documentId > 0) this.document.id = documentId;
      this.loading = false;
      this.recognizeFields();
      this.$v.$reset();
      this.checkPermission();
    },
    recognizeFields() {
      if (
        this.document.hasAutomaticRecognition &&
        this.attachments.length > 0
      ) {
        this.recognizing = true;
        this.recognisedFields = [];
        this.notRecognisedFields = [];
        var documentDto = this.document.toDto(
          Utils.object.toJS(this.attachments)
        );
        DocumentService.getAutomaticRecognitionFields(
          this.contentTypeId,
          documentDto
        ).then((fields) => {
          fields.forEach((field) => {
            if (field.Value != "" && field.Value != null) {
              this.document.fields.find((x) => x.code == field.Code).value =
                field.Value;
              this.recognisedFields.push(field.Code);
            } else {
              this.notRecognisedFields.push(field.Code);
            }
          });
          this.recognizing = false;
        });
      }
    },
    onFileSuccess(file, response) {
      this.attachments.map((a) => {
        if (a.fileName === file.name) {
          let responseForThisFile = response.filter((r) => r.text == file.name)[0];
          a.isLoading = false;
          a.fileId = responseForThisFile.id;
          a.canDisplay = responseForThisFile.isFileFormatSupportedForDisplay;

          if (a.canDisplay) this.loadFile(a);
        }
      });

      this.recognizeFields();
    },
    async onFileAdded(file) {
      if (
        this.attachments.filter((f) =>
          Utils.text.contains(f.fileName, file.name)
        ).length > 0
      ) {
        Utils.toast.error(this.$t("DocumentDuplicatedError"));
        this.dropZone.removeFile(file);
        return;
      }

      if (this.attachments.length >= this.dropzoneOptions.maxFiles) {
        this.dropZone.removeFile(file);
        return;
      }

      var extension = file.name.split(".").pop();

      if (
        this.dropzoneOptions.acceptedFiles
          .split(",")
          .filter((f) => f.toUpperCase() == `.${extension}`.toUpperCase())
          .length == 0
      ) {
        Utils.toast.error(this.$t("DropzoneInvalidFileType"));
        this.dropZone.removeFile(file);
        return;
      }
      let attachment = new Attachment();
      attachment.fileId = parseInt(Math.random().toString().slice(2));
      attachment.fileName = file.name;
      attachment.isLoading = true;
      attachment.size = file.size;
      attachment.extension = extension;
      attachment.thumbnail = Utils.file.image.generateAvatar(attachment.extension,150);
      attachment.canDisplay = false;

      this.attachments = [...this.attachments, attachment];
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      if (this.document.id <= 0 && this.attachments.length == 0) {
        Utils.toast.error(this.$t("DocumentAttachmentRequired"));
        return;
      }
      if (this.attachments.filter((f) => f.isLoading).length > 0) return;

      var uniqueFields = [
        ...new Set(
          this.document.fields
            .filter((f) => f.isUnique && f.value != "")
            .map((f) => f.title)
        ),
      ];
      if (this.attachments.length > 1 && uniqueFields.length > 0) {
        var confirmMessage = this.$t(
          "UniqueFieldsPossibleDuplicatesWarning"
        ).replaceAll("{0}", uniqueFields.join());

        var res = await this.$confirm(confirmMessage, {
          title: this.$t("Confirm"),
          icon: "mdi-alert",
          color: "warning",
          buttonTrueText: this.$t("Save"),
          buttonFalseText: this.$t("Cancel"),
        });

        if (res != 1) {
          return;
        }
      }

      var documentDto = this.document.toDto(
        Utils.object.toJS(this.attachments)
      );

      this.saving = true;

      DocumentService.save(documentDto)
        .then((result) => {
          if (this.document.id > 0) {
            Utils.toast.success(this.$t("DocumentUpdatedSuccessfully"));
            this.$emit("on-document-updated", {
              oldObjectID: this.document.id,
              newObjectID: result[0],
            });
          } else if (this.isBasketDocument) {
            this.$emit("on-document-created");
          } else {
            this.createdDocumentsIds = result;
            this.showDocumentCreatedToast = true;
            var searchResultRoute = Utils.text.contains(
              this.$route.name,
              "search"
            );
            if (searchResultRoute) {
              EventBus.$emit("refreshDocumentList");
            }
          }

          this.hide();
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.saving = false;
        });
    },
    checkPermission() {
      DocumentService.canAddDataToInternalTables(this.contentTypeId).then(
        (result) => {
          this.canAddData = result;
        }
      );
    },
    async new() {
      this.dropzoneOptions.maxFiles = 20;
      this.dropzoneOptions.dictDefaultMessage = this.$t("DropzoneMessage");
      this.document = new Document();
      this.isDragAndDropDocument = false;
      this.isBasketDocument = false;
      this.showViewer = false;
      this.attachments = [];
      this.recognisedFields = [];
      this.notRecognisedFields = [];
      this.originalAttachment = {
        fileId: 0,
        fileName: "",
        isLoading: false,
        size: 0,
        extension: "",
        canDisplay: false,
      };
      this.contentTypeId = null;
      this.$v.$reset();
      await this.loadContentTypeList();
      if (this.contentTypeList.length == 1) {
        this.contentTypeId = this.contentTypeList[0].id;
        this.loadFields();
      }
      this.show();

      this.$nextTick(() => {
        this.attachments = [];
        this.dropZone.removeAllFiles();
      });
    },
    async edit(documentId) {
      this.$v.$reset();
      this.dropzoneOptions.maxFiles = 1;
      this.dropzoneOptions.dictDefaultMessage = this.$t("UpdateAttachment");
      this.loading = true;
      this.recognisedFields = [];
      this.notRecognisedFields = [];
      this.isDragAndDropDocument = false;
      this.isBasketDocument = false;
      this.showViewer = false;
      await this.loadContentTypeList();
      DocumentService.get(documentId).then((dto) => {
        this.document = new Document(dto);
        this.attachments = [];
        let original = new Attachment();
        var extension = dto.AttachmentFileName.split(".").pop();
        original.fileId = this.document.id;
        original.fileName = dto.AttachmentFileName;
        original.isLoading = false;
        original.size = 0;
        original.extension = extension;
        original.thumbnail = Utils.file.image.generateAvatar(
          original.extension,
          150
        );
        original.canDisplay = false;
        DocumentService.canDisplay(documentId).then((data) => {
          original.canDisplay = data;
          this.originalAttachment = original;
          if (original.canDisplay) this.loadFile(original);
        });

        if (
          !this.contentTypeList.some(
            (ct) => ct.id == this.document.contentType.id
          )
        ) {
          this.contentTypeList.push(this.document.contentType);
        }
        this.contentTypeId = this.document.contentType.id;
        this.checkPermission();
        this.loading = false;
      });

      this.show();

      this.$nextTick(() => {
        this.attachments = [];
        this.dropZone.removeAllFiles();
      });
    },
    clone(documentId, files) {
      this.$v.$reset();
      this.loadContentTypeList();
      this.dropzoneOptions.maxFiles = 20;
      this.dropzoneOptions.dictDefaultMessage = this.$t("DropzoneMessage");
      this.loading = true;
      this.isDragAndDropDocument = true;
      this.isBasketDocument = false;
      this.showViewer = false;
      DocumentService.get(documentId).then((dto) => {
        this.document = new Document(dto);
        this.document.id = 0;
        this.contentTypeId = this.document.contentType.id;
        this.checkPermission();
        this.loading = false;
      });

      this.show();

      this.$nextTick(() => {
        this.attachments = [];
        this.dropZone.removeAllFiles();
        this.dropZone.dropzone.handleFiles(files);
      });
    },
    async createFromBasket(contentTypeId, filter, files) {
      this.$v.$reset();
      this.contentTypeId = contentTypeId;
      await this.loadContentTypeList();
      await this.loadFields();
      this.dropzoneOptions.maxFiles = 20;
      this.dropzoneOptions.dictDefaultMessage = this.$t("DropzoneMessage");
      this.isDragAndDropDocument = false;
      this.isBasketDocument = true;
      this.showViewer = false;

      this.show();

      this.$nextTick(() => {
        this.attachments = [];
        this.dropZone.removeAllFiles();
        this.dropZone.dropzone.handleFiles(files);

        if (filter) {
          this.loading = true;
          let conditionItems = [];
          filter.split(";").forEach((condition) => {
            var conditionItem = new ConditionItem(condition);
            conditionItems.push(conditionItem);
          });

          for (let i = 0; i < this.document.fields.length; i++) {
            const documentField = this.document.fields[i];
            var condition = conditionItems.filter(
              (c) =>
                c.fieldCode == documentField.code &&
                (c.searchOperatorCode == "s04" ||
                  c.searchOperatorCode == "d05" ||
                  c.searchOperatorCode == "n05" ||
                  c.searchOperatorCode == "l01")
            );
            if (condition.length == 1) {
              this.document.fields[i].value = condition[0].fieldValue;
            }
          }

          this.loading = false;
        }
      });
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
    thumbnail: function (file, dataUrl) {
      if (Utils.text.contains(file.type, "image")) {
        this.attachments.map((a) => {
          if (a.fileName === file.name) {
            a.thumbnail = dataUrl;
          }
        });
      }
    },
    loadFile(file) {
      this.selectedFileTitle = file.fileName;
      let fileUrl = encodeURIComponent(
        DocumentService.displayTempPdf(file.fileId, file.extension)
      );

      if (this.originalAttachment.fileId == file.fileId) {
        fileUrl = encodeURIComponent(DocumentService.display(file.fileId));
      }

      this.viewerInstance.load(fileUrl);
      this.showViewer = true;
    },
    removeFile(file) {
      this.cleanRecognisedFields();
      this.attachments = this.attachments.filter(
        (a) => a.fileId != file.fileId
      );
      DocumentService.deleteTempPdf(file.fileId);
      this.$nextTick(() => {
        this.dropZone.removeAllFiles();
      });
    },
    cleanRecognisedFields() {
      let fieldsValueToSave = [];
      for (let l = 0; l < this.document.fields.length; l++) {
        if (!this.recognisedFields.includes(this.document.fields[l].code)) {
          fieldsValueToSave.push(this.document.fields[l]);
        }
      }
      this.loadFields(true).then(() => {
        if (fieldsValueToSave.length > 0) {
          this.loading = true;
          for (let k = 0; k < this.document.fields.length; k++) {
            for (let i = 0; i < fieldsValueToSave.length; i++) {
              if (this.document.fields[k].code == fieldsValueToSave[i].code) {
                this.document.fields[k].value = fieldsValueToSave[i].value;
                break;
              }
            }
          }
          this.$nextTick(() => {
            this.loading = false;
            this.recognisedFields = [];
            this.notRecognisedFields = [];
          });
        }
      });
    },
    dragover(event) {
      event.preventDefault();
      this.hoverDropzone = true;
    },
    dragleave(event) {
      event.preventDefault();
      this.hoverDropzone = false;
    },
    viewCreatedDocuments() {
      var pattern = {
        searchPattern: this.createdDocumentsIds.join(),
        searchAllVersions: false,
      };
      var route = `/search/${SearchType.DocumentIDs}/${encodeURIComponent(
        JSON.stringify(pattern)
      )}`;
      this.showDocumentCreatedToast = false;
      this.$router.push(route);
    },
  }
};
</script>